import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { KeyRound, LifeBuoy, ShieldCheck, Trash2, UserCheck } from "lucide-react";
import { removeInstanceKey } from "../../api/recovery.js";
import type { RecoveryAdmin, RecoveryOverview } from "../../api/recovery.js";
import { grantInstanceKeyTo, setUpInstanceKey } from "../../app/adminRecovery.js";
import { describeAuthError } from "../../app/authFlow.js";
import { useSession } from "../../app/session.js";
import { ensureIdentity } from "../../app/teamKeys.js";
import { ADMIN_AUDIT_KEY, ADMIN_RECOVERY_KEY, ADMIN_USERS_KEY, ME_KEY, useRecoveryOverview } from "../../data/account.js";
import { formatDateTime, formatRelativeTime } from "../../lib/format.js";
import { Alert, Badge, Button, Card, CardHeader, ConfirmInline, EmptyState, Skeleton } from "../../ui/primitives.js";
import { useToast } from "../../ui/Toast.js";
import { Fingerprint } from "../recovery/Fingerprint.js";

/** Admin: the instance recovery key – set up, hand on to other admins, remove. */
export function RecoveryPanel(props: { readonly selfId: string }): ReactNode {
  const { state } = useSession();
  const queryClient = useQueryClient();
  const [identityReady, setIdentityReady] = useState(false);
  const overview = useRecoveryOverview(identityReady);

  // Every admin who opens this tab gets an identity key pair, so a holder can hand the key to them.
  useEffect((): void => {
    if (state.status !== "unlocked") {
      return;
    }
    void ensureIdentity(state.client).catch((): void => undefined).finally((): void => setIdentityReady(true));
  }, [state]);

  function refresh(next: RecoveryOverview | null): void {
    queryClient.setQueryData<RecoveryOverview | null>(ADMIN_RECOVERY_KEY, next);
    void queryClient.invalidateQueries({ queryKey: ME_KEY });
    void queryClient.invalidateQueries({ queryKey: ADMIN_USERS_KEY });
    void queryClient.invalidateQueries({ queryKey: ADMIN_AUDIT_KEY });
  }

  if (!identityReady || overview.isPending) {
    return <Skeleton className="h-72" />;
  }
  if (overview.error !== null) {
    return <Alert tone="danger">{overview.error.message}</Alert>;
  }
  return overview.data === null ? <SetUp onChanged={refresh} /> : <KeyDetails overview={overview.data} selfId={props.selfId} onChanged={refresh} />;
}

const EXPLAINER = "Hat jemand das Master-Passwort vergessen und kein Notfall-Kit, kann ein Administrator den Zugang zurücksetzen. Dazu versiegelt jede registrierte App die Kontoschlüssel für den Wiederherstellungsschlüssel der Instanz. Wer diesen Schlüssel hält, kann damit die Tresore registrierter Konten öffnen – gib ihn nur Administratoren, denen alle vertrauen.";

function SetUp(props: { readonly onChanged: (next: RecoveryOverview | null) => void }): ReactNode {
  const { state } = useSession();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  async function create(): Promise<void> {
    if (state.status !== "unlocked" || busy) {
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      props.onChanged(await setUpInstanceKey(state.client));
      toast.notify("Admin-Wiederherstellung eingerichtet.", { tone: "success" });
    } catch (err: unknown) {
      setError(describeAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <EmptyState icon={LifeBuoy} title="Admin-Wiederherstellung ist aus" action={
        <Button icon={KeyRound} loading={busy} onClick={(): void => void create()}>
          Einrichten
        </Button>
      }>
        {EXPLAINER}
      </EmptyState>
      {error !== undefined ? <Alert tone="danger">{error}</Alert> : null}
    </div>
  );
}

function KeyDetails(props: {
  readonly overview: RecoveryOverview;
  readonly selfId: string;
  readonly onChanged: (next: RecoveryOverview | null) => void;
}): ReactNode {
  const { state } = useSession();
  const toast = useToast();
  const [busy, setBusy] = useState<string | undefined>(undefined);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const overview = props.overview;
  const holding = overview.sealedKeyB64 !== null;

  async function grant(admin: RecoveryAdmin): Promise<void> {
    if (state.status !== "unlocked" || busy !== undefined) {
      return;
    }
    setBusy(admin.userId);
    setError(undefined);
    try {
      props.onChanged(await grantInstanceKeyTo(state.client, overview, admin));
      toast.notify(`${admin.email} hält jetzt den Wiederherstellungsschlüssel.`, { tone: "success" });
    } catch (err: unknown) {
      setError(describeAuthError(err));
    } finally {
      setBusy(undefined);
    }
  }

  async function remove(): Promise<void> {
    if (busy !== undefined) {
      return;
    }
    setBusy("remove");
    setError(undefined);
    try {
      await removeInstanceKey(overview.keyId);
      props.onChanged(null);
      toast.notify("Admin-Wiederherstellung entfernt.", { tone: "success" });
    } catch (err: unknown) {
      setError(describeAuthError(err));
      setConfirmRemove(false);
    } finally {
      setBusy(undefined);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader icon={LifeBuoy} title="Admin-Wiederherstellung" description={EXPLAINER} />
        <dl className="grid gap-4 px-5 py-4 text-sm sm:grid-cols-3">
          <div>
            <dt className="text-xs text-mist-2">Fingerabdruck</dt>
            <dd className="mt-1"><Fingerprint publicKeyB64={overview.publicKeyB64} /></dd>
          </div>
          <div>
            <dt className="text-xs text-mist-2">Eingerichtet</dt>
            <dd className="mt-1 text-foam" title={formatDateTime(overview.createdAt)}>
              {formatRelativeTime(overview.createdAt, Date.now())}
              {overview.createdByEmail !== null ? ` von ${overview.createdByEmail}` : ""}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-mist-2">Registrierte Konten</dt>
            <dd className="mt-1 text-foam">{overview.enrolled} von {overview.accounts}</dd>
          </div>
        </dl>
        <p className="border-t border-line px-5 py-3 text-[0.8125rem] leading-relaxed text-mist-2">
          Nutzer sehen denselben Fingerabdruck in ihren Einstellungen. Weicht er ab, hat jemand den Schlüssel ausgetauscht.
        </p>
      </Card>

      {!holding ? (
        <Alert tone="info">Du hältst den Wiederherstellungsschlüssel nicht. Ein Administrator, der ihn hat, kann ihn dir hier freigeben.</Alert>
      ) : null}

      <Card>
        <CardHeader icon={ShieldCheck} title="Schlüsselhalter" description="Administratoren, die Konten zurücksetzen können. Jeder Halter kann den Schlüssel an weitere Administratoren freigeben." />
        <ul className="divide-y divide-line">
          {overview.admins.map((admin: RecoveryAdmin) => (
            <li key={admin.userId} className="flex flex-col gap-2 px-5 py-3 sm:flex-row sm:items-center">
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-2 truncate text-sm font-medium text-foam">
                  <span className="truncate">{admin.email}</span>
                  {admin.userId === props.selfId ? <Badge tone="solid">Du</Badge> : null}
                </p>
                <p className="text-[0.8125rem] text-mist-2">
                  {admin.holder && admin.grantedAt !== null
                    ? `hält den Schlüssel seit ${formatRelativeTime(admin.grantedAt, Date.now())}`
                    : admin.identityPubB64 === null
                      ? "noch kein Schlüsselpaar – entsteht beim ersten Öffnen dieses Bereichs"
                      : "hält den Schlüssel nicht"}
                </p>
              </div>
              {admin.holder ? (
                <Badge tone="mint" icon={UserCheck}>Halter</Badge>
              ) : holding ? (
                <Button
                  variant="secondary"
                  size="sm"
                  icon={KeyRound}
                  loading={busy === admin.userId}
                  disabled={admin.identityPubB64 === null || (busy !== undefined && busy !== admin.userId)}
                  onClick={(): void => void grant(admin)}
                >
                  Freigeben
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      </Card>

      {error !== undefined ? <Alert tone="danger">{error}</Alert> : null}

      {confirmRemove ? (
        <ConfirmInline
          message="Alle Registrierungen gehen verloren. Ein vergessenes Master-Passwort lässt sich danach nur noch mit dem Notfall-Kit zurücksetzen. Ein neuer Schlüssel braucht neue Registrierungen."
          confirmLabel="Endgültig entfernen"
          pending={busy === "remove"}
          onConfirm={(): void => void remove()}
          onCancel={(): void => setConfirmRemove(false)}
        />
      ) : (
        <div className="flex justify-end">
          <Button variant="ghost" icon={Trash2} onClick={(): void => setConfirmRemove(true)}>
            Admin-Wiederherstellung entfernen
          </Button>
        </div>
      )}
    </div>
  );
}
